import { NuqsAdapter } from 'nuqs/adapters/react-router/v7';
import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { ClientErrorReporter } from '@/components/admin/ClientErrorReporter';
import { ServerErrorWatcher } from '@/components/admin/ServerErrorWatcher';
import { BugReportWidget } from '@/components/BugReportWidget';
import { Toaster } from '@/components/ui/sonner';
import { formatBreadcrumbs, installCrashBreadcrumbs, lastAction } from '@/lib/crashBreadcrumbs';
import { BpDialogHost } from '@/lib/dialogs';
import { ActivityPanel, ActivityProvider } from './components/ActivityTracker';
import { AppLayout } from './components/AppLayout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AuthProvider, useAuth } from './lib/auth';
import { AcceptInvitePage } from './pages/AcceptInvitePage';
import { ContactPage } from './pages/ContactPage';
import { LandingPage } from './pages/LandingPage';
import { LoginPage } from './pages/LoginPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';

// Start recording the interaction trail as early as possible so a crash on the
// first render still has context for the ErrorBoundary report.
installCrashBreadcrumbs();

// Lazy routes — kept off the main bundle. Each one pulls a heavy dep
// (Monaco / FlexLayout / ECharts / TipTap) or is otherwise rarely the first
// page a user lands on. Eager routes above are: Login, ResetPassword,
// AcceptInvite — auth-flow surfaces that have no heavy deps. Everything
// else (including Issues and Releases) defers vendor-echarts off the
// initial bundle. Suspense fallback is just a blank background so we
// don't flash a spinner during the chunk fetch.
const IssuesPage = lazy(() =>
  import('./pages/IssuesPage').then((m) => ({ default: m.IssuesPage })),
);
const CodeReviewsPage = lazy(() =>
  import('./pages/CodeReviewsPage').then((m) => ({ default: m.CodeReviewsPage })),
);
const ReleasesPage = lazy(() =>
  import('./pages/ReleasesPage').then((m) => ({ default: m.ReleasesPage })),
);
const SessionViewer = lazy(() =>
  import('./pages/SessionViewer').then((m) => ({ default: m.SessionViewer })),
);
const ProjectsPage = lazy(() =>
  import('./pages/ProjectsPage').then((m) => ({ default: m.ProjectsPage })),
);
const ProjectAdminPage = lazy(() =>
  import('./pages/ProjectAdminPage').then((m) => ({ default: m.ProjectAdminPage })),
);
const AdminPage = lazy(() => import('./pages/AdminPage').then((m) => ({ default: m.AdminPage })));
const UsersPage = lazy(() => import('./pages/UsersPage').then((m) => ({ default: m.UsersPage })));
const AgentsPage = lazy(() =>
  import('./pages/AgentsPage').then((m) => ({ default: m.AgentsPage })),
);
const ScriptsPage = lazy(() =>
  import('./pages/ScriptsPage').then((m) => ({ default: m.ScriptsPage })),
);
const ArtifactsPage = lazy(() =>
  import('./pages/ArtifactsPage').then((m) => ({ default: m.ArtifactsPage })),
);
const DebugToolsPage = lazy(() =>
  import('./pages/DebugToolsPage').then((m) => ({ default: m.DebugToolsPage })),
);
const ReleaseDetailPage = lazy(() =>
  import('./pages/ReleaseDetailPage').then((m) => ({ default: m.ReleaseDetailPage })),
);
const DocsPage = lazy(() => import('./pages/DocsPage').then((m) => ({ default: m.DocsPage })));
const IssueRulesPage = lazy(() =>
  import('./pages/IssueRulesPage').then((m) => ({ default: m.IssueRulesPage })),
);
const AttachmentRulesPage = lazy(() =>
  import('./pages/AttachmentRulesPage').then((m) => ({ default: m.AttachmentRulesPage })),
);
const IssueWorkflowPage = lazy(() =>
  import('./pages/IssueWorkflowPage').then((m) => ({ default: m.IssueWorkflowPage })),
);
const IssueMergeReviewPage = lazy(() =>
  import('./pages/IssueMergeReviewPage').then((m) => ({ default: m.IssueMergeReviewPage })),
);
const PerformancePage = lazy(() =>
  import('./pages/PerformancePage').then((m) => ({ default: m.PerformancePage })),
);
const AnalyticsPage = lazy(() =>
  import('./pages/AnalyticsPage').then((m) => ({ default: m.AnalyticsPage })),
);
const CoveragePage = lazy(() => import('./pages/CoveragePage'));
const CoverageMapPage = lazy(() => import('./pages/CoverageMapPage'));
const GameConfigPage = lazy(() =>
  import('./pages/GameConfigPage').then((m) => ({ default: m.GameConfigPage })),
);
const ChatsPage = lazy(() => import('./pages/ChatsPage').then((m) => ({ default: m.ChatsPage })));
const FeedbackPage = lazy(() =>
  import('./pages/FeedbackPage').then((m) => ({ default: m.FeedbackPage })),
);
const TasksPage = lazy(() => import('./pages/TasksPage').then((m) => ({ default: m.TasksPage })));
const QaQueuePage = lazy(() =>
  import('./pages/QaQueuePage').then((m) => ({ default: m.QaQueuePage })),
);
const LogsPage = lazy(() => import('./pages/LogsPage').then((m) => ({ default: m.LogsPage })));
const GroupedLogsPage = lazy(() =>
  import('./pages/GroupedLogsPage').then((m) => ({ default: m.GroupedLogsPage })),
);
const LinkedSessionsPage = lazy(() =>
  import('./pages/LinkedSessionsPage').then((m) => ({ default: m.LinkedSessionsPage })),
);
const HttpMetricsPage = lazy(() =>
  import('./pages/HttpMetricsPage').then((m) => ({ default: m.HttpMetricsPage })),
);
const LiveMonitorPage = lazy(() =>
  import('./pages/LiveMonitorPage').then((m) => ({ default: m.LiveMonitorPage })),
);
const ToolsPage = lazy(() => import('./pages/ToolsPage').then((m) => ({ default: m.ToolsPage })));
const SharedIssuePage = lazy(() =>
  import('./pages/SharedIssuePage').then((m) => ({ default: m.SharedIssuePage })),
);
const SharedFeedbackPage = lazy(() =>
  import('./pages/SharedFeedbackPage').then((m) => ({ default: m.SharedFeedbackPage })),
);
const SharedLogPage = lazy(() =>
  import('./pages/SharedLogPage').then((m) => ({ default: m.SharedLogPage })),
);
const WikiPage = lazy(() => import('./pages/WikiPage').then((m) => ({ default: m.WikiPage })));
const TestsPage = lazy(() => import('./pages/TestsPage').then((m) => ({ default: m.TestsPage })));
const SessionScreensPage = lazy(() =>
  import('./pages/SessionScreensPage').then((m) => ({ default: m.SessionScreensPage })),
);
const ControlSessionPage = lazy(() =>
  import('./pages/ControlSessionPage').then((m) => ({ default: m.ControlSessionPage })),
);

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null; componentStack: string | null; copied: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: null, copied: false };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // getDerivedStateFromError can't see the component stack — only
    // componentDidCatch gets it, and for a minified Base UI / React error
    // the component stack is the only part that actually localises the bug.
    this.setState({ componentStack: info.componentStack ?? null });
    // Re-log so it also lands in devtools and the ClientErrorReporter path.
    console.error('ErrorBoundary caught:', error, info.componentStack);
  }
  // One copy-paste blob with everything a maintainer needs to triage: error,
  // full JS stack, React component stack, the page, the last interaction, the
  // recent breadcrumb trail, and session/runtime context.
  private buildReport(): string {
    const { error, componentStack } = this.state;
    const ls = (k: string) => {
      try {
        return localStorage.getItem(k) || '(none)';
      } catch {
        return '(blocked)';
      }
    };
    // perf.memory is non-standard (Chromium only) — guard it.
    const mem = (
      performance as unknown as { memory?: { usedJSHeapSize: number; jsHeapSizeLimit: number } }
    ).memory;
    const memStr = mem
      ? `${(mem.usedJSHeapSize / 1048576).toFixed(0)} / ${(mem.jsHeapSizeLimit / 1048576).toFixed(0)} MB`
      : '(unavailable)';
    return [
      'Bugpunch dashboard crash',
      '',
      `Error:        ${error?.name ?? 'Error'}: ${error?.message ?? 'unknown'}`,
      `Page (route): ${window.location.pathname}${window.location.search}${window.location.hash}`,
      `Full URL:     ${window.location.href}`,
      `Last action:  ${lastAction()}`,
      `Time:         ${new Date().toISOString()}`,
      `Uptime:       ${(performance.now() / 1000).toFixed(1)}s since load`,
      '',
      `Project:      ${ls('active_project_id')}`,
      `Org:          ${ls('active_org_id')}`,
      `Signed in:    ${ls('auth_token') === '(none)' ? 'no' : 'yes'}`,
      `Document:     ${document.title}`,
      `Referrer:     ${document.referrer || '(none)'}`,
      '',
      `User agent:   ${navigator.userAgent}`,
      `Language:     ${navigator.language}`,
      `Online:       ${navigator.onLine}`,
      `Viewport:     ${window.innerWidth}×${window.innerHeight} @ ${window.devicePixelRatio}x`,
      `Screen:       ${window.screen.width}×${window.screen.height}`,
      `JS heap:      ${memStr}`,
      `Build:        ${import.meta.env.MODE}`,
      '',
      '── Recent actions (oldest → newest) ──',
      formatBreadcrumbs(),
      '',
      '── Stack ──',
      error?.stack ?? '(no stack)',
      '',
      '── Component stack ──',
      componentStack ?? '(no component stack)',
    ].join('\n');
  }
  private copy = async () => {
    const text = this.buildReport();
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Clipboard API blocked (insecure context / permissions) — fall back
      // to the legacy execCommand path so the button still works.
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
      } catch {
        /* give up silently */
      }
      document.body.removeChild(ta);
    }
    this.setState({ copied: true });
    window.setTimeout(() => this.setState({ copied: false }), 2000);
  };
  render() {
    if (this.state.hasError) {
      const report = this.buildReport();
      return (
        <div className="bg-background text-foreground min-h-screen flex flex-col items-center justify-center p-6">
          <div className="w-full max-w-3xl flex flex-col gap-4">
            <div>
              <h1 className="text-2xl font-semibold">Something went wrong</h1>
              <p className="text-destructive mt-1 break-words">
                {this.state.error?.name ?? 'Error'}: {this.state.error?.message ?? 'Unknown error'}
              </p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={this.copy}
                className="px-4 py-2 bg-primary text-primary-foreground border-none rounded-md cursor-pointer"
              >
                {this.state.copied ? 'Copied ✓' : 'Copy full details'}
              </button>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-secondary text-secondary-foreground border border-border rounded-md cursor-pointer"
              >
                Reload
              </button>
            </div>

            {/* Full report, also visible so it can be selected manually if the
                clipboard is blocked. Scrollable, monospace, wraps long lines. */}
            <pre className="text-xs leading-relaxed bg-muted text-muted-foreground rounded-md border border-border p-3 max-h-[55vh] overflow-auto whitespace-pre-wrap break-words select-text">
              {report}
            </pre>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function NotFound() {
  return (
    <div className="bg-background text-foreground min-h-screen flex flex-col items-center justify-center">
      <h1 className="text-5xl m-0">404</h1>
      <p className="text-muted-foreground mt-2">Page not found</p>
      <a href="/" className="mt-4 text-primary">
        Go to Releases
      </a>
    </div>
  );
}

// Root gate: signed-out visitors get the public marketing landing page;
// signed-in users are sent to their dashboard (Releases). Keeps `/` doubling
// as both the marketing front door and the app home depending on auth.
function RootGate() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#0a0b0d]" />;
  return user ? <Navigate to="/releases" replace /> : <LandingPage />;
}

function App() {
  return (
    <>
      <ErrorBoundary>
        <BrowserRouter>
          <NuqsAdapter>
            <AuthProvider>
              <ActivityProvider>
                <Toaster />
                <ActivityPanel />
                <ServerErrorWatcher />
                <ClientErrorReporter />
                <Suspense fallback={<div className="bg-background min-h-screen" />}>
                  <Routes>
                    {/* Public marketing surface — no auth. `/` shows the landing page
                to signed-out visitors and redirects signed-in users to the
                dashboard (see RootGate). */}
                    <Route path="/" element={<RootGate />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                    <Route path="/accept-invite" element={<AcceptInvitePage />} />
                    <Route path="/docs" element={<DocsPage />} />
                    <Route path="/docs/:sectionId" element={<DocsPage />} />
                    <Route path="/docs/:sectionId/:pageId" element={<DocsPage />} />
                    {/* Public share — no auth required. Route is mounted outside the
                ProtectedRoute wrapper so an external collaborator with just
                the URL can open the issue. */}
                    <Route path="/share/issue/:id" element={<SharedIssuePage />} />
                    <Route path="/share/feedback/:id" element={<SharedFeedbackPage />} />
                    <Route path="/share/log/:token" element={<SharedLogPage />} />
                    <Route
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <Outlet />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    >
                      {/* `/` is handled publicly by RootGate above; the dashboard home
                  lives at `/releases`. */}
                      <Route path="/projects" element={<ProjectsPage />} />
                      <Route path="/project-admin" element={<ProjectAdminPage />} />
                      <Route path="/session/:id" element={<SessionViewer />} />
                      <Route path="/issues" element={<IssuesPage />} />
                      <Route path="/code-reviews" element={<CodeReviewsPage />} />
                      <Route path="/triage" element={<TasksPage />} />
                      <Route path="/tasks" element={<TasksPage />} />
                      <Route path="/tasks/list" element={<TasksPage />} />
                      <Route path="/tasks/people" element={<TasksPage />} />
                      <Route path="/qa-queue" element={<QaQueuePage />} />
                      <Route path="/testing/qa" element={<QaQueuePage />} />
                      <Route path="/chats" element={<ChatsPage />} />
                      <Route path="/feedback" element={<FeedbackPage />} />
                      <Route path="/performance" element={<PerformancePage />} />
                      <Route path="/analytics" element={<AnalyticsPage />} />
                      <Route path="/coverage" element={<CoverageMapPage />} />
                      <Route path="/qa-goals" element={<CoveragePage />} />
                      <Route path="/game-config" element={<GameConfigPage />} />
                      <Route path="/admin" element={<AdminPage />} />
                      <Route path="/admin/http-metrics" element={<HttpMetricsPage />} />
                      <Route path="/admin/live" element={<LiveMonitorPage />} />
                      <Route path="/users" element={<UsersPage />} />
                      {/* Back-compat: old Devices route now lands on Users. */}
                      <Route path="/devices" element={<UsersPage />} />
                      <Route path="/logs" element={<LogsPage />} />
                      <Route path="/logs/grouped" element={<GroupedLogsPage />} />
                      <Route path="/linked-sessions" element={<LinkedSessionsPage />} />
                      <Route path="/logs/:sessionId" element={<LogsPage />} />
                      <Route path="/agents" element={<AgentsPage />} />
                      <Route path="/runners" element={<AgentsPage />} />
                      <Route path="/testing/tests" element={<TestsPage />} />
                      <Route
                        path="/testing/live"
                        element={<Navigate to="/testing/tests" replace />}
                      />
                      <Route
                        path="/testing/results"
                        element={<Navigate to="/testing/tests" replace />}
                      />
                      <Route
                        path="/testing/replays"
                        element={<Navigate to="/testing/tests" replace />}
                      />
                      <Route
                        path="/testing/scenarios"
                        element={<Navigate to="/testing/tests?tab=library" replace />}
                      />
                      <Route path="/testing/sessions/:id" element={<ControlSessionPage />} />
                      <Route path="/testing/sessions/:id/screens" element={<SessionScreensPage />} />
                      <Route path="/scripts" element={<ScriptsPage />} />
                      <Route path="/tools" element={<ToolsPage />} />
                      <Route path="/tools/:tab" element={<ToolsPage />} />
                      <Route path="/releases" element={<ReleasesPage />} />
                      <Route path="/releases/:id" element={<ReleaseDetailPage />} />
                      <Route path="/artifacts" element={<ArtifactsPage />} />
                      <Route path="/debug-tools" element={<DebugToolsPage />} />
                      <Route path="/crashes/fingerprint-settings" element={<IssueRulesPage />} />
                      <Route path="/issues/rules" element={<IssueRulesPage />} />
                      <Route path="/issues/attachment-rules" element={<AttachmentRulesPage />} />
                      <Route path="/issues/workflow" element={<IssueWorkflowPage />} />
                      <Route path="/issues/merge-review" element={<IssueMergeReviewPage />} />
                      <Route path="/settings" element={<AdminPage />} />
                      <Route path="/wiki" element={<WikiPage />} />
                      <Route path="*" element={<NotFound />} />
                    </Route>
                  </Routes>
                </Suspense>
              </ActivityProvider>
            </AuthProvider>
          </NuqsAdapter>
        </BrowserRouter>
      </ErrorBoundary>
      {/* Bug report widget — outside ErrorBoundary so it works even when the app crashes */}
      <BugReportWidget />
      <BpDialogHost />
    </>
  );
}

export default App;
